import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export type MatchStatus = 'all' | 'upcoming' | 'live' | 'completed' | 'finished';

interface HasStatus {
  status?: string;
}

export function useStatusFilter<T extends HasStatus>(
  items: T[]
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = (searchParams.get('status') || 'all') as MatchStatus;
  const [status, setStatus] = useState<MatchStatus>(statusFromUrl);

  // keep status in URL in sync
  useEffect(() => {
    const current = searchParams.get('status') || 'all';
    if (current !== status) {
      const next = new URLSearchParams(searchParams);
      if (status === 'all') {
        next.delete('status');
      } else {
        next.set('status', status);
      }
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const normalizedStatus = (s?: string): 'upcoming' | 'live' | 'completed' | undefined => {
    if (!s) return undefined;
    if (s === 'finished') return 'completed';
    if (s === 'completed') return 'completed';
    if (s === 'upcoming' || s === 'live') return s;
    return undefined;
  };

  const counts = useMemo(() => {
    const base = { upcoming: 0, live: 0, completed: 0 };
    for (const it of items) {
      const s = normalizedStatus(it.status);
      if (!s) continue;
      // @ts-ignore - keyof base is safe here
      base[s] += 1;
    }
    return base;
  }, [items]);

  const filteredItems = useMemo(() => {
    if (status === 'all') return items;
    const target = status === 'finished' ? 'completed' : status;
    return items.filter((it) => normalizedStatus(it.status) === target);
  }, [items, status]);

  return { status, setStatus, filteredItems, counts } as const;
}


